import React, { useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import { submitApplication, fileToBase64 } from '../../../services/jobApplicationService';

const DAY_TICKETS_BANNER_CDN =
  'https://BelvoirCastlePull.b-cdn.net/Website/1.webp';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 3rem 2rem 5rem;
  animation: ${fadeUp} 0.5s ease;

  @media (max-width: 768px) {
    padding: 2rem 1.25rem 4rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: ${props => props.theme.colors.accent};
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  margin-bottom: 2.5rem;
  transition: gap 0.2s ease;

  &:hover { gap: 0.7rem; }
`;

const JobHeader = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2.5rem 3rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  margin-bottom: 2rem;
  border-top: 4px solid ${props => props.theme.colors.accent};

  @media (max-width: 768px) {
    padding: 1.75rem 1.5rem;
  }
`;

const JobTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.4rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const JobSubtitle = styled.p`
  color: ${props => props.theme.colors.accent};
  font-weight: 700;
  font-size: 1.05rem;
  margin: 0 0 1.5rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 2rem;
  margin-bottom: 1.75rem;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 160px;
`;

const MetaLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.primary};
  opacity: 0.55;
`;

const MetaValue = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  line-height: 1.4;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

const Tag = styled.span`
  display: inline-block;
  padding: 0.35rem 0.9rem;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  background: ${props => props.highlight
    ? props.theme.colors.primary
    : 'rgba(16,29,64,0.07)'};
  color: ${props => props.highlight ? 'white' : props.theme.colors.primary};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 2rem;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem 2.5rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    padding: 1.5rem 1.25rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid rgba(16,29,64,0.08);
`;

const BodyText = styled.p`
  font-size: 1.05rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;

  &:last-child { margin-bottom: 0; }
`;

const SubHeading = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.15rem;
  color: ${props => props.theme.colors.primary};
  margin: 1.5rem 0 0.6rem;
`;

const BulletList = styled.ul`
  font-size: 1rem;
  line-height: 1.7;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
  padding-left: 1.4rem;

  li { margin-bottom: 0.35rem; }
`;

const NumberedList = styled.ol`
  font-size: 1rem;
  line-height: 1.7;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
  padding-left: 1.4rem;

  li { margin-bottom: 0.35rem; }
`;

const StickyCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  border-top: 4px solid ${props => props.theme.colors.primary};
  position: sticky;
  top: 140px;
`;

const CardTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.3rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  font-size: 0.92rem;
  margin-bottom: 0.4rem;

  span.req { color: ${props => props.theme.colors.accent}; }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.7rem 0.9rem;
  border: 2px solid rgba(16,29,64,0.12);
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &::placeholder { color: #aaa; }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.7rem 0.9rem;
  border: 2px solid rgba(16,29,64,0.12);
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &::placeholder { color: #aaa; }
`;

const FileUploadArea = styled.label`
  display: block;
  border: 2px dashed ${props => props.hasFile
    ? props.theme.colors.primary
    : 'rgba(16,29,64,0.2)'};
  border-radius: 10px;
  padding: 1rem 1.25rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.hasFile
    ? 'rgba(16,29,64,0.04)'
    : 'transparent'};

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: rgba(16,29,64,0.03);
  }

  input { display: none; }
`;

const FileUploadText = styled.div`
  font-size: 0.88rem;
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.selected ? '600' : '500'};
  opacity: ${props => props.selected ? 1 : 0.6};
`;

const FileUploadHint = styled.div`
  font-size: 0.82rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.5;
  margin-top: 0.35rem;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(16,29,64,0.08);
  margin: 1.25rem 0;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: ${props => props.theme.colors.primary};
  color: white;
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: ${props => props.theme.fonts.main};
  letter-spacing: 0.02em;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(16,29,64,0.2);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusMessage = styled.div`
  margin-top: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  background: ${props => props.type === 'success'
    ? '#d4edda'
    : props.type === 'error'
    ? '#f8d7da'
    : '#d1ecf1'};
  color: ${props => props.type === 'success'
    ? '#155724'
    : props.type === 'error'
    ? '#721c24'
    : '#0c5460'};
  border: 1px solid ${props => props.type === 'success'
    ? '#c3e6cb'
    : props.type === 'error'
    ? '#f5c6cb'
    : '#bee5eb'};
`;

const SmallNote = styled.p`
  font-size: 0.8rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.6;
  line-height: 1.5;
  margin: 0.5rem 0 0;
  text-align: center;
`;

const SuccessWrap = styled.div`
  text-align: center;
  padding: 2rem 0;
`;

const SuccessIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const SuccessTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.4rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
`;

const SuccessText = styled.p`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.75;
  line-height: 1.6;
  margin: 0;
`;

const EventsOrganiser = () => {
  const formRef = useRef(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    portfolio: '',
    coverNote: '',
  });
  const [cvFile, setCvFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = e => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (setter) => (e) => {
    const file = e.target.files[0] || null;
    setter(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    if (!cvFile) {
      setStatus({ type: 'error', message: 'Please attach your CV.' });
      return;
    }
    if (!coverFile) {
      setStatus({ type: 'error', message: 'Please attach your cover letter.' });
      return;
    }
    if (!form.coverNote.trim()) {
      setStatus({ type: 'error', message: 'Please include a cover note.' });
      return;
    }

    setSubmitting(true);
    try {
      const [cvData, coverData] = await Promise.all([
        fileToBase64(cvFile),
        fileToBase64(coverFile),
      ]);

      await submitApplication({
        roleId: 'events-organiser',
        roleTitle: 'Events Organiser — Belvoir Castle',
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        portfolio: form.portfolio,
        coverNote: form.coverNote,
        cvData,
        cvFileName: cvFile.name,
        coverLetterData: coverData,
        coverLetterFileName: coverFile.name,
      });

      setSubmitted(true);
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <ParallaxBanner
        title="Events Organiser"
        subtitle="Belvoir Castle & Retail Village · Leicestershire"
        image={DAY_TICKETS_BANNER_CDN}
      />

      <ContentContainer>
        <BackLink to="/about/careers">← Back to Careers</BackLink>

        <JobHeader>
          <JobTitle>Events Organiser</JobTitle>
          <JobSubtitle>Belvoir Castle &amp; Belvoir Castle Retail Village</JobSubtitle>
          <MetaRow>
            <MetaItem>
              <MetaLabel>Salary</MetaLabel>
              <MetaValue>£28,000 – £30,000 per year</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Reports To</MetaLabel>
              <MetaValue>Managing Director</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Contract</MetaLabel>
              <MetaValue>Permanent</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Working Pattern</MetaLabel>
              <MetaValue>Weekend work required</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Location</MetaLabel>
              <MetaValue>Belvoir Castle, Leicestershire</MetaValue>
            </MetaItem>
          </MetaRow>
          <TagRow>
            <Tag highlight>Now Hiring</Tag>
            <Tag>Events</Tag>
            <Tag>Permanent</Tag>
            <Tag>Weekend Work</Tag>
            <Tag>Commercial</Tag>
          </TagRow>
        </JobHeader>

        <Grid>
          <div>
            <Section>
              <SectionTitle>About Belvoir Castle</SectionTitle>
              <BodyText>
                Belvoir Castle is the historic stately home of the Duke and Duchess of Rutland. The beautiful
                Leicestershire estate is situated between the towns of Grantham and Melton Mowbray. Belvoir
                Castle&apos;s vision is to share the history and beauty of the Estate through delivering amazing
                experiences to both our local community and visitors from further afield.
              </BodyText>
              <BodyText>
                Our aim is to preserve, grow and develop the Estate in a sustainable way so that it can be shared
                and enjoyed in the present, but also for future generations to come. The Castle is a seven day a
                week operation, and the Events Organiser is responsible for delivering a seasonal programme of
                events, both directly, and in partnership with third party promoters and organisers.
              </BodyText>
            </Section>

            <Section>
              <SectionTitle>The Role</SectionTitle>
              <BodyText>
                Are you someone who thinks out of the box and is passionate about delivering amazing events?
                Are you a self-starter who loves a challenge and embraces no two days being the same? We are
                looking for someone to take responsibility for our events programme at Belvoir Castle and
                Belvoir Castle Retail Village, to drive the business both financially and creatively and execute
                our events to the highest possible standards.
              </BodyText>
              <BodyText>
                The events programme changes with the seasons — through the spring and summer you&apos;ll be
                working with our Visitor Experience team to create events that animate the castle and its
                extraordinary heritage, ensure the highest level of service, and the magical Belvoir experience
                throughout. Finally, you&apos;ll invent exciting ways to bring our new Adventure Playground to
                life, with themed weekends, and kid friendly events that drive new footfall from our local
                community.
              </BodyText>
              <BodyText>
                Your close collaborators will be drawn from across the Estate, and you will report to the
                Managing Director. You&apos;ll work closely with our outsourced Events Manager to ensure a
                terrific customer journey, and our Head of Belvoir Castle Opening Operations to see that planning
                and delivery are in perfect harmony. Throughout, you will have oversight of commercial
                performance, evaluating events for their contribution to the business, and ensuring that the
                events programme develops in a strategic and profitable way. There is significant room for
                growth, and the right candidate will have an entrepreneurial spirit, and a get up and go attitude.
              </BodyText>
            </Section>

            <Section>
              <SectionTitle>Key Responsibilities</SectionTitle>
              <BulletList>
                <li>Develop the concept and creation of public facing events for Belvoir Castle and Belvoir Castle Retail Village.</li>
                <li>Lead and manage the delivery of all public facing events, working closely with the outsourced Events Manager and Operations Manager as well as the wider Estate team.</li>
                <li>Ensure a smooth day to day running of the events, working with the castle&apos;s catering, retail and hospitality staff.</li>
                <li>Manage and maximise event budgets ensuring that income targets are met.</li>
                <li>Liaise with the local government and councils to ensure that appropriate licences and permissions are in place, and maintain good relations with local Parish Councils to ensure effective communication with local communities.</li>
                <li>Liaise closely with the marketing team to ensure that events are promoted in a timely and effective manner across all media.</li>
                <li>Ensure thorough evaluation of the events programme, reporting to the MD.</li>
              </BulletList>
            </Section>

            <Section>
              <SectionTitle>Person Specification</SectionTitle>
              <BulletList>
                <li>A seasoned professional, with previous experience in events, or a similar related field.</li>
                <li>A self-starter, with plenty of drive and determination to achieve results.</li>
                <li>Familiar with managing budgets and ensuring good cost control.</li>
                <li>Able to negotiate across a wide spectrum of stakeholders, both internally and externally, to achieve the smooth delivery of an event.</li>
              </BulletList>
            </Section>

            <Section>
              <SectionTitle>What We Offer</SectionTitle>
              <BulletList>
                <li>A permanent role with significant room for growth.</li>
                <li>Salary of £28,000 – £30,000 per year depending on experience.</li>
                <li>The chance to shape a creative, commercially driven events programme at a historic estate.</li>
                <li>Close collaboration with teams across the Castle, Retail Village and wider Estate.</li>
              </BulletList>
            </Section>
          </div>

          <StickyCard ref={formRef}>
            {submitted ? (
              <SuccessWrap>
                <SuccessIcon>✓</SuccessIcon>
                <SuccessTitle>Application Received</SuccessTitle>
                <SuccessText>
                  Thank you for your application. We'll be in touch if your experience matches what we're looking for.
                </SuccessText>
              </SuccessWrap>
            ) : (
              <>
                <CardTitle>Apply for This Role</CardTitle>
                <form onSubmit={handleSubmit}>
                  <FormGroup>
                    <Label>First Name <span className="req">*</span></Label>
                    <Input
                      type="text"
                      name="firstName"
                      value={form.firstName}
                      onChange={handleChange}
                      placeholder="Jane"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Last Name <span className="req">*</span></Label>
                    <Input
                      type="text"
                      name="lastName"
                      value={form.lastName}
                      onChange={handleChange}
                      placeholder="Smith"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Email Address <span className="req">*</span></Label>
                    <Input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="jane@example.com"
                      required
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Phone Number</Label>
                    <Input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+44 7700 900000"
                    />
                  </FormGroup>

                  <Divider />

                  <FormGroup>
                    <Label>CV <span className="req">*</span></Label>
                    <FileUploadArea hasFile={!!cvFile}>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange(setCvFile)}
                      />
                      <FileUploadText selected={!!cvFile}>
                        {cvFile ? cvFile.name : 'Choose file — CV'}
                      </FileUploadText>
                      {!cvFile && (
                        <FileUploadHint>PDF, DOC or DOCX · Max 10MB</FileUploadHint>
                      )}
                    </FileUploadArea>
                  </FormGroup>

                  <FormGroup>
                    <Label>Cover Letter <span className="req">*</span></Label>
                    <FileUploadArea hasFile={!!coverFile}>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange(setCoverFile)}
                      />
                      <FileUploadText selected={!!coverFile}>
                        {coverFile ? coverFile.name : 'Choose file — Cover Letter'}
                      </FileUploadText>
                      {!coverFile && (
                        <FileUploadHint>PDF, DOC or DOCX · Max 10MB</FileUploadHint>
                      )}
                    </FileUploadArea>
                  </FormGroup>

                  <Divider />

                  <FormGroup>
                    <Label>Cover Note <span className="req">*</span></Label>
                    <TextArea
                      name="coverNote"
                      value={form.coverNote}
                      onChange={handleChange}
                      placeholder="Tell us about your events experience, how you approach creative programming, and why you'd like to join Belvoir Castle."
                      rows={5}
                      required
                    />
                    <SmallNote>Applications without a cover note will not be considered.</SmallNote>
                  </FormGroup>

                  {status && (
                    <StatusMessage type={status.type}>{status.message}</StatusMessage>
                  )}

                  <SubmitButton type="submit" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit Application'}
                  </SubmitButton>

                  <SmallNote style={{ marginTop: '0.85rem' }}>
                    Your information is kept confidential and used only for this recruitment process.
                  </SmallNote>
                </form>
              </>
            )}
          </StickyCard>
        </Grid>
      </ContentContainer>

      <Footer />
    </PageContainer>
  );
};

export default EventsOrganiser;
